import { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { FormField } from "./FormField";
import { WalletManager } from "../WalletManager";
import { LockToggle } from "./LockToggle";
import { useWalletState } from "@/hooks/useWalletState";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export function TaxDistribution() {
  const { watch, setValue, getValues } = useFormContext();
  const { address: connectedWallet, isTestnet } = useWalletState();

  // Watch all relevant form values
  const buyTax = watch("buyTax");
  const sellTax = watch("sellTax");
  const customBuyTax = watch("customBuyTax");
  const customSellTax = watch("customSellTax");
  const taxDistribution = watch("taxDistribution");
  const hasReflection = watch("hasReflection");
  const hasAutoLiquidity = watch("hasAutoLiquidity");
  const hasBurn = watch("hasBurn");
  const hasMarketing = watch("hasMarketing");
  const hasDevelopment = watch("hasDevelopment");

  const effectiveBuyTax = buyTax === "custom"
    ? parseFloat(customBuyTax) || 0
    : parseFloat(buyTax) || 0;

  const effectiveSellTax = sellTax === "custom"
    ? parseFloat(customSellTax) || 0
    : parseFloat(sellTax) || 0;

  const hasTax = effectiveBuyTax > 0 || effectiveSellTax > 0;

  const [taxTypes, setTaxTypes] = useState(() => ({
    reflection: {
      label: "Reflection Distribution",
      tooltip: "The percentage of collected tax allocated to reflection",
      value: "0",
      disabled: !hasReflection
    },
    autoLiquidity: {
      label: "Auto-Liquidity Distribution",
      tooltip: "The percentage of collected tax allocated to auto-liquidity",
      value: "0",
      disabled: !hasAutoLiquidity
    },
    burn: {
      label: "Burn Distribution",
      tooltip: "The percentage of collected tax allocated to burn",
      value: "0",
      disabled: !hasBurn
    },
    marketing: {
      label: "Marketing Wallet Distribution",
      tooltip: "The percentage of collected tax allocated to marketing wallet",
      value: "0",
      disabled: !hasMarketing
    },
    development: {
      label: "Development Wallet Distribution",
      tooltip: "The percentage of collected tax allocated to development wallet",
      value: "0",
      disabled: !hasDevelopment
    },
  }));

  const [customValues, setCustomValues] = useState({
    reflection: "",
    autoLiquidity: "",
    burn: "",
    marketing: "",
    development: "",
  });

  // Update local state when form values change
  useEffect(() => {
    if (taxDistribution) {
      Object.entries(taxDistribution).forEach(([key, value]) => {
        if (key !== "marketingWallets" && key !== "developmentWallets") {
          setTaxTypes(prev => ({
            ...prev,
            [key]: {
              ...prev[key],
              value: value || "0"
            }
          }));
        }
      });
    }
  }, [taxDistribution]);

  const handleTaxTypeChange = (type: keyof typeof taxTypes, value: string) => {
    if (value === "custom") {
      // Update both states when switching to custom
      setTaxTypes(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          value: "custom"
        }
      }));

      // Initialize custom value with current value or 0
      const currentValue = taxTypes[type].value === "custom"
        ? customValues[type]
        : taxTypes[type].value || "0";

      setCustomValues(prev => ({
        ...prev,
        [type]: currentValue
      }));

      // Update form
      setValue(`taxDistribution.${type}`, currentValue);
    } else {
      // For preset values
      setTaxTypes(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          value
        }
      }));

      // Clear custom value
      setCustomValues(prev => ({
        ...prev,
        [type]: ""
      }));

      // Update form
      setValue(`taxDistribution.${type}`, value);
    }
  };

  const handleCustomValueChange = (type: keyof typeof taxTypes, value: string) => {
    // Update custom value
    setCustomValues(prev => ({
      ...prev,
      [type]: value
    }));

    // Ensure tax type stays as custom
    setTaxTypes(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        value: "custom"
      }
    }));

    // Update form
    setValue(`taxDistribution.${type}`, value);
  };

  const [showTaxWarning, setShowTaxWarning] = useState(false);
  const [marketingWallets, setMarketingWallets] = useState<string[]>([connectedWallet || ""]);
  const [developmentWallets, setDevelopmentWallets] = useState<string[]>([connectedWallet || ""]);

  // Initialize wallets from form values
  useEffect(() => {
    const currentMarketingWallets = getValues("marketingWallets");
    const currentDevelopmentWallets = getValues("developmentWallets");

    if (currentMarketingWallets?.length) {
      setMarketingWallets(currentMarketingWallets);
    }
    if (currentDevelopmentWallets?.length) {
      setDevelopmentWallets(currentDevelopmentWallets);
    }
  }, []);

  // Update form when wallets change
  useEffect(() => {
    setValue("marketingWallets", marketingWallets);
  }, [marketingWallets, setValue]);

  useEffect(() => {
    setValue("developmentWallets", developmentWallets);
  }, [developmentWallets, setValue]);

  // Update local state when form values change
  useEffect(() => {
    const currentMarketingWallets = getValues("marketingWallets");
    const currentDevelopmentWallets = getValues("developmentWallets");

    if (JSON.stringify(currentMarketingWallets) !== JSON.stringify(marketingWallets)) {
      setMarketingWallets(currentMarketingWallets || [connectedWallet || ""]);
    }
    if (JSON.stringify(currentDevelopmentWallets) !== JSON.stringify(developmentWallets)) {
      setDevelopmentWallets(currentDevelopmentWallets || [connectedWallet || ""]);
    }
  }, [watch("marketingWallets"), watch("developmentWallets")]);

  const [hasSetDefaultWallets, setHasSetDefaultWallets] = useState(false);

  useEffect(() => {
    // Only set default wallets once when tax is first enabled
    if (connectedWallet && hasTax && !hasSetDefaultWallets) {
      let shouldSetDefaults = false;

      if (taxTypes.marketing.value !== "0" && (!marketingWallets.length || !marketingWallets[0])) {
        setMarketingWallets([connectedWallet]);
        setValue('marketingWallets', [connectedWallet]);
        shouldSetDefaults = true;
      }
      if (taxTypes.development.value !== "0" && (!developmentWallets.length || !developmentWallets[0])) {
        setDevelopmentWallets([connectedWallet]);
        setValue('developmentWallets', [connectedWallet]);
        shouldSetDefaults = true;
      }

      if (shouldSetDefaults) {
        setHasSetDefaultWallets(true);
      }
    }
  }, [connectedWallet, hasTax, taxTypes.marketing.value, taxTypes.development.value]);

  const validateDistribution = () => {
    if (!hasTax) return true;
    const total = Object.entries(taxTypes).reduce((sum, [key, value]) => {
      const effectiveValue = value.value === "custom"
        ? parseFloat(customValues[key as keyof typeof customValues]) || 0
        : parseFloat(value.value) || 0;
      return sum + effectiveValue;
    }, 0);
    return total === 100;
  };

  useEffect(() => {
    if (hasTax && !validateDistribution()) {
      setShowTaxWarning(true);
    }
  }, [hasTax, taxTypes, customValues]);

  const getRemainingTaxColor = () => {
    if (!hasTax) return "text-muted-foreground";
    const remainingTax = calculateRemainingTax();
    if (remainingTax === 0) return "text-green-500";
    if (remainingTax < 0) return "text-red-500";
    return "text-yellow-500";
  };

  const hasMarketingTax = () => {
    return (parseFloat(taxTypes.marketing.value) > 0 || parseFloat(customValues.marketing) > 0) && hasTax;
  };

  const hasDevelopmentTax = () => {
    return (parseFloat(taxTypes.development.value) > 0 || parseFloat(customValues.development) > 0) && hasTax;
  };

  const getRemainingDistributionStyle = () => {
    const remaining = calculateRemainingTax();
    return {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      border: '2px solid',
      borderColor: remaining === 0 ? '#22c55e' : '#f97316', // green-500 when 0, orange-500 otherwise
      transition: 'border-color 0.3s ease'
    };
  };

  const getDistributionWarningStyle = () => {
    const remaining = calculateRemainingTax();
    return {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      border: '2px solid',
      borderColor: remaining === 0 ? '#22c55e' : '#f97316', // green-500 when 0, orange-500 otherwise
      transition: 'border-color 0.3s ease'
    };
  };

  const calculateRemainingTax = () => {
    const totalDistribution = Object.entries(taxTypes).reduce((total, [key, value]) => {
      const effectiveValue = value.value === "custom"
        ? parseFloat(customValues[key as keyof typeof customValues]) || 0
        : parseFloat(value.value) || 0;
      return total + effectiveValue;
    }, 0);
    return 100 - totalDistribution;
  };

  const renderTaxTypeField = (type: string, label: string, tooltip: string, disabled: boolean) => {
    const taxType = taxTypes[type];
    const isCustom = taxType.value === "custom";
    const customValue = customValues[type];

    return (
      <FormField
        label={
          <div className="flex items-center justify-between">
            <span>{label}</span>
            {isTestnet && (
              <LockToggle
                locked={watch(`taxDistribution.${type}Locked`)}
                onToggle={(locked) => setValue(`taxDistribution.${type}Locked`, locked)}
                tooltip={`If locked, ${type} distribution settings cannot be changed after token deployment`}
              />
            )}
          </div>
        }
        tooltip={tooltip}
        input={
          <div className="space-y-2">
            <Select
              value={taxType.value}
              onValueChange={(value) => handleTaxTypeChange(type as keyof typeof taxTypes, value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={`Select ${type} distribution`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">0%</SelectItem>
                <SelectItem value="5">5%</SelectItem>
                <SelectItem value="10">10%</SelectItem>
                <SelectItem value="15">15%</SelectItem>
                <SelectItem value="20">20%</SelectItem>
                <SelectItem value="25">25%</SelectItem>
                <SelectItem value="50">50%</SelectItem>
                <SelectItem value="75">75%</SelectItem>
                <SelectItem value="100">100%</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            {taxType.value === "custom" && (
              <Input
                type="number"
                placeholder="Enter custom percentage (0-100)"
                value={customValue}
                min="0"
                max="100"
                step="1"
                onChange={(e) => handleCustomValueChange(type as keyof typeof taxTypes, e.target.value)}
              />
            )}
          </div>
        }
      />
    );
  };

  if (!hasTax) {
    return (
      <div className="text-center text-muted-foreground py-4">
        No tax distribution needed when tax rates are 0%
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hasTax && (
        <>
          {/* Tax Distribution Warning */}
          <div
            className="p-4 rounded-md text-sm font-medium"
            style={getDistributionWarningStyle()}
          >
            Total tax distribution must reach 100% when a buy/sell tax is selected
          </div>

          {/* Custom Tax Inputs */}
          {buyTax === "custom" && (
            <FormField
              label="Custom Buy Tax"
              tooltip="Enter a custom buy tax percentage"
              input={
                <Input
                  type="number"
                  placeholder="Enter custom buy tax (0-100)"
                  value={customBuyTax || ""}
                  min="0"
                  max="100"
                  step="1"
                  onChange={(e) => setValue("customBuyTax", e.target.value)}
                />
              }
            />
          )}

          {sellTax === "custom" && (
            <FormField
              label="Custom Sell Tax"
              tooltip="Enter a custom sell tax percentage"
              input={
                <Input
                  type="number"
                  placeholder="Enter custom sell tax (0-100)"
                  value={customSellTax || ""}
                  min="0"
                  max="100"
                  step="1"
                  onChange={(e) => setValue("customSellTax", e.target.value)}
                />
              }
            />
          )}

          {/* Distribution Types */}
          {Object.entries(taxTypes).map(([key, data]) => (
            renderTaxTypeField(key, data.label, data.tooltip, data.disabled)
          ))}

          {/* Wallet Management */}
          {hasMarketingTax() && (
            <WalletManager
              label="Marketing Wallets"
              wallets={marketingWallets}
              onChange={setMarketingWallets}
              locked={false}
              onLockToggle={(locked) => setValue("marketingWalletsLocked", locked)}
              type="marketing"
              maxWallets={1}
            />
          )}

          {hasDevelopmentTax() && (
            <WalletManager
              label="Development Wallets"
              wallets={developmentWallets}
              onChange={setDevelopmentWallets}
              locked={false}
              onLockToggle={(locked) => setValue("developmentWalletsLocked", locked)}
              type="development"
              maxWallets={1}
            />
          )}

          {/* Remaining Distribution */}
          <div
            className="p-4 rounded-md"
            style={getRemainingDistributionStyle()}
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Remaining Distribution</span>
              <span className="font-bold">{calculateRemainingTax()}%</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}